import { useEffect } from "react";

// Hostnames that represent the real, indexable production site.
const PRODUCTION_HOSTS = new Set(["enzoscleaning.com", "www.enzoscleaning.com"]);

/**
 * Client-side noindex guard for every non-production hostname.
 *
 * The staging mirror (2.enzoscleaning.com) is served by Lovable's own hosting
 * (behind Cloudflare) — confirmed by response headers (server: cloudflare,
 * x-deployment-id, no Vercel headers). That hosting runs NEITHER Vercel Edge
 * Middleware (middleware.ts is Vercel-only) NOR Cloudflare Pages Functions, so
 * neither server-side approach can noindex it. And because every hostname is
 * served the exact same prerendered static HTML, a noindex can't be baked into
 * that HTML without also hiding production.
 *
 * The one lever that works regardless of who hosts the page is client-side:
 * Googlebot renders JavaScript on its second indexing wave and honors a robots
 * meta tag injected into the live DOM. On production hosts this is a no-op (no
 * tag added). On any other hostname (staging, preview deploys, local dev) it
 * injects <meta name="robots" content="noindex, nofollow"> so the page is
 * dropped from search results while staying fully usable for QA.
 *
 * This is defense-in-depth alongside middleware.ts (which still covers Vercel
 * preview deploys at the edge). Runs only in the browser — during Node
 * prerendering `window` doesn't exist and this component isn't in the SSR tree,
 * so production's prerendered HTML is never touched.
 */
export const StagingNoindex = () => {
  useEffect(() => {
    if (PRODUCTION_HOSTS.has(window.location.hostname)) return;

    let tag = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", "noindex, nofollow");
  }, []);

  return null;
};
